import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirstTouchAttribution } from '../utils/attribution';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Enrich Sentry events with the current user's id, email and plan.
// Clears the context when called with `null`. Sentry is lazy-loaded
// (async chunk — see index.js) so we resolve it dynamically here.
const setSentryUserContext = (userObj) => {
  import('@sentry/react')
    .then((Sentry) => {
      if (!userObj) {
        Sentry.setUser(null);
        return;
      }
      Sentry.setUser({
        id: userObj.id,
        email: userObj.email,
      });
      Sentry.setTag('user_plan', userObj.subscription_plan || 'trial');
      Sentry.setTag('user_status', userObj.subscription_status || 'unknown');
    })
    .catch(() => {
      // Sentry not configured — ignore
    });
};

// Map FastAPI error shapes (string | array | object) to a renderable string.
const formatApiErrorDetail = (detail, fallback) => {
  if (detail == null) return fallback || 'Algo salió mal. Intenta de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reads (and clears) the redirect stashed before an auth flow, e.g. by the
  // Pricing page ("/planes?checkout=viajero") or SharedRoutePage.
  const consumePostLoginRedirect = () => {
    try {
      const stashed = sessionStorage.getItem('post_login_redirect');
      if (stashed && stashed.startsWith('/')) {
        sessionStorage.removeItem('post_login_redirect');
        return stashed;
      }
    } catch { /* private mode */ }
    return '/dashboard';
  };
  const [error, setError] = useState(null);

  // On mount, check session via httpOnly cookies. No localStorage involved.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setUser(data);
          setSentryUserContext(data);
        }
      } catch (err) {
        // Not authenticated — fine, the app will show landing/login
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const register = async (email, password, confirmPassword, fullName = '', phone = '', referralCode = '', homeCountry = '') => {
    setError(null);
    const attribution = getFirstTouchAttribution() || {};
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email,
        password,
        confirm_password: confirmPassword,
        full_name: fullName,
        phone,
        ...(referralCode ? { referral_code: referralCode } : {}),
        ...(homeCountry ? { home_country: homeCountry } : {}),
        ...(attribution.utm_source ? { utm_source: attribution.utm_source } : {}),
        ...(attribution.utm_medium ? { utm_medium: attribution.utm_medium } : {}),
        ...(attribution.utm_campaign ? { utm_campaign: attribution.utm_campaign } : {}),
        ...(attribution.referrer_domain ? { referrer_domain: attribution.referrer_domain } : {}),
        ...(attribution.landing_path ? { landing_path: attribution.landing_path } : {}),
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      const msg = formatApiErrorDetail(data.detail, 'Error al registrarse');
      setError(msg);
      throw new Error(msg);
    }
    // Session cookie is set by the backend; honour any stashed redirect
    // (plan checkout, shared-route clone…) before defaulting to dashboard.
    window.location.href = consumePostLoginRedirect();
    return data;
  };

  const login = async (email, password) => {
    setError(null);
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      const msg = formatApiErrorDetail(data.detail, 'Error al iniciar sesión');
      setError(msg);
      throw new Error(msg);
    }
    window.location.href = consumePostLoginRedirect();
    return data;
  };

  const refreshAccessToken = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch (_) {
      return false;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/email-logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setSentryUserContext(null);
    }
  };

  const requestPasswordReset = async (email) => {
    setError(null);
    const res = await fetch(`${API_URL}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      let data = {}; try { data = await res.json(); } catch (err) { console.debug('request-password-reset: non-JSON error body', err); }
      const msg = formatApiErrorDetail(data.detail, 'Error al solicitar restablecimiento');
      setError(msg);
      throw new Error(msg);
    }
    return await res.json();
  };

  const resetPassword = async (token, newPassword, confirmPassword) => {
    setError(null);
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    });
    if (!res.ok) {
      let data = {}; try { data = await res.json(); } catch (err) { console.debug('reset-password: non-JSON error body', err); }
      const msg = formatApiErrorDetail(data.detail, 'Error al restablecer contraseña');
      setError(msg);
      throw new Error(msg);
    }
    return await res.json();
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      register,
      login,
      logout,
      refreshAccessToken,
      requestPasswordReset,
      resetPassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
